/**
 * EndSessionConfirmDialog Component
 *
 * Accessible confirmation modal for the parent-controlled "End session" flow
 * (SR6 / US-006 / US-007). Cloned from ReportIssueDialog (accessible overlay
 * pattern: role=dialog, aria-modal, auto-focus, focus-trap, Escape-to-dismiss)
 * and adapted with the SA-resolved 1-coin copy.
 *
 * Implements TS-007 T7 (AC-TS007.4).
 * Clones: ReportIssueDialog overlay pattern (focus management, keyboard handling,
 *         card markup) — frontend/src/components/ReportIssueDialog.tsx:58-177.
 *
 * BR-TS007.2: the End action on the live card/listing ONLY opens this dialog —
 * ending requires this explicit confirm click (no one-click end).
 *
 * Props are fully controlled — no internal data fetching or navigation.
 * TO-LL-013: navigation never happens from inside this dialog or on end success
 * (useEndSession, T3, deliberately does not navigate either).
 *
 * House style: copy contains no dashes.
 */

import { useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

/**
 * Props for EndSessionConfirmDialog
 */
export interface EndSessionConfirmDialogProps {
  /** Controls visibility; returns null when false (lazy DOM, cloned from ReportIssueDialog) */
  isVisible: boolean;

  /** True while the end mutation is in-flight; disables both buttons + shows spinner */
  isSubmitting: boolean;

  /**
   * Set to a mapped message when the end API call fails, so the parent can retry.
   * Do NOT expose raw backend error text (security: no internal detail leakage).
   */
  errorMessage?: string | null;

  /** Called when the parent confirms ending the session */
  onConfirm: () => void;

  /** Called when the parent dismisses without ending (Escape, Keep going, overlay) */
  onDismiss: () => void;
}

/**
 * EndSessionConfirmDialog — full-screen accessible modal for the parent-controlled
 * end-session flow.
 *
 * Features (cloned from ReportIssueDialog):
 *   - role="dialog" aria-modal="true" with aria-label
 *   - Auto-focus on the confirm button when opened (via useEffect + 50ms delay)
 *   - Escape key calls onDismiss
 *   - Tab focus-trap cycles between confirm and dismiss buttons
 *   - Returns null when isVisible=false (no DOM cost until opened)
 *
 * SA-resolved exact copy (BR-TS007.2, no dashes):
 *   Title: "End this session now?"
 *   Body: "Ending this session will use 1 coin. Your child will see that you ended
 *          it, and no session report or star will be created. Any pebbles already
 *          earned are kept."
 *   Confirm: "End session (1 coin)" (spinner: "Ending...")
 *   Cancel: "Keep going"
 */
export function EndSessionConfirmDialog({
  isVisible,
  isSubmitting,
  errorMessage,
  onConfirm,
  onDismiss,
}: EndSessionConfirmDialogProps) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const dismissButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the confirm button when dialog opens (cloned from ReportIssueDialog)
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Keyboard handling: Escape → dismiss; Tab → focus-trap between two buttons
  // Cloned from ReportIssueDialog's focus-trap pattern.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
      // Focus-trap: cycle Tab between confirm and dismiss buttons
      if (e.key === 'Tab') {
        e.preventDefault();
        if (document.activeElement === confirmButtonRef.current) {
          dismissButtonRef.current?.focus();
        } else {
          confirmButtonRef.current?.focus();
        }
      }
    },
    [onDismiss]
  );

  // Don't render if not visible — no dialog DOM until opened (cloned from ReportIssueDialog)
  if (!isVisible) {
    return null;
  }

  return (
    /* Overlay — same pattern as ReportIssueDialog */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-label="End this session now?"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Centered card — cloned from ReportIssueDialog */}
      <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 text-center max-w-sm mx-4">
        {/* Heading */}
        <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
          End this session now?
        </h2>

        {/* SA-resolved exact copy — no dashes (house style) */}
        <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4">
          Ending this session will use 1 coin. Your child will see that you ended it, and no
          session report or star will be created. Any pebbles already earned are kept.
        </p>

        {/* Inline error — shown when the end API call fails; mapped text (no leakage) */}
        {errorMessage && (
          <p className="text-sm text-red-600 mb-4" role="alert">
            {errorMessage}
          </p>
        )}

        {/* Action buttons */}
        <div className="flex flex-col gap-3">
          {/* Confirm: End session (1 coin) — explicit confirm required (BR-TS007.2) */}
          <Button
            ref={confirmButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onConfirm();
            }}
            disabled={isSubmitting}
            className="w-full h-11"
            variant="destructive"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Ending...
              </>
            ) : (
              'End session (1 coin)'
            )}
          </Button>

          {/* Dismiss: Keep going */}
          <Button
            ref={dismissButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            disabled={isSubmitting}
            variant="outline"
            className="w-full h-11"
          >
            Keep going
          </Button>
        </div>
      </div>
    </div>
  );
}
